import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'

export const SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES = [OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON]
export const DEFAULT_CHANNEL_MARKUP_VALUE = 1
const ROUND_UP_MULTIPLE = 10
const ALLOWED_UTM_SOURCE = ['impact_radius', 'google', 'facebook']

export type ChannelMarkupDetails = {
  markup: number
  isApplied: boolean
}

export type ChannelMarkupEvaluation = ChannelMarkupDetails & {
  offerId: string
  offerType?: App.OfferType
  offerLabel?: string
  isSupported: boolean
  unsupportedReason?: string
  evaluations: Array<{
    name: string
    passed: boolean
    reason?: string
  }>
}

const NO_CHANNEL_MARKUP: ChannelMarkupDetails = { markup: DEFAULT_CHANNEL_MARKUP_VALUE, isApplied: false }

/**
 * Round a numeric value up to the nearest multiple of 10.
 * Examples:
 * - 4198.95 -> 4200
 * - 3255.34 -> 3260
 * - 770     -> 770
 *
 * Accepts number or numeric string input to mirror floatify-style helpers.
 */
export function roundUpToNearestTen(value: number | string): number {
  const num = parseFloat(value as any)
  if (!Number.isFinite(num)) {
    // Mirror the behaviour of parseFloat-style helpers: return NaN (or +/-Infinity) as-is.
    return num
  }
  return Math.ceil(num / ROUND_UP_MULTIPLE) * ROUND_UP_MULTIPLE
}

function isOfferEligibleForChannelMarkup(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): boolean {
  if (!offerType || !channelMarkup) {
    return false
  }
  if (channelMarkup.channelMarkupBlacklist.includes(offerId)) {
    return false
  }
  // LPP (bedbank) channel markup is behind its own kill-switch, mirroring svc-order's
  // ENABLE_CHANNEL_MARKUP_FOR_LPP flag, so it can be rolled out / reverted independently.
  if (offerType === OFFER_TYPE_BED_BANK && !config.ENABLE_CHANNEL_MARKUP_FOR_LPP) {
    return false
  }
  // LPC (tactical_ao_hotel) channel markup is behind its own kill-switch (off in production)
  // until the validate-base / charge-marked flow through svc-reservation is fully verified.
  if (offerType === OFFER_TYPE_ALWAYS_ON && !config.ENABLE_CHANNEL_MARKUP_FOR_LPC) {
    return false
  }
  return SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES.includes(offerType)
}

export function getChannelMarkupDetails(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): ChannelMarkupDetails {
  if (!isOfferEligibleForChannelMarkup(offerId, offerType, channelMarkup)) {
    return NO_CHANNEL_MARKUP
  }

  const channelMarkupValue = channelMarkup!.channelMarkupValue
  const isApplied = !!channelMarkupValue && channelMarkupValue !== DEFAULT_CHANNEL_MARKUP_VALUE
  return {
    markup: isApplied ? channelMarkupValue : DEFAULT_CHANNEL_MARKUP_VALUE,
    isApplied,
  }
}

export function getChannelMarkupEvaluation(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
  offerLabel?: string,
): ChannelMarkupEvaluation {
  const details = getChannelMarkupDetails(offerId, offerType, channelMarkup)

  const isSupportedOfferType = !!offerType && SUPPORTED_CHANNEL_MARKUP_OFFER_TYPES.includes(offerType)
  const isBlacklisted = !!channelMarkup && channelMarkup.channelMarkupBlacklist.includes(offerId)
  const isLppDisabledByConfig = offerType === OFFER_TYPE_BED_BANK && !config.ENABLE_CHANNEL_MARKUP_FOR_LPP
  const isLpcDisabledByConfig = offerType === OFFER_TYPE_ALWAYS_ON && !config.ENABLE_CHANNEL_MARKUP_FOR_LPC
  const configAllowsChannelMarkup = !isLppDisabledByConfig && !isLpcDisabledByConfig
  const configDisabledReason = isLppDisabledByConfig
    ? 'LPP channel markup is disabled by config.'
    : isLpcDisabledByConfig
      ? 'LPC channel markup is disabled by config.'
      : undefined

  // Single source for evaluations, unsupportedReason and isSupported below.
  // `reason` may be undefined on a failed check when an earlier check carries it.
  const checks: Array<{ name: string, passed: boolean, reason?: string }> = [
    {
      name: 'offer type present',
      passed: !!offerType,
      reason: offerType ? undefined : 'Offer type is missing.',
    },
    {
      name: 'channel markup present',
      passed: !!channelMarkup,
      reason: channelMarkup ? undefined : 'No channel markup is loaded for this offer.',
    },
    {
      name: 'offer type supported',
      passed: isSupportedOfferType,
      reason: !offerType || isSupportedOfferType
        ? undefined
        : `Offer type "${offerType}" does not support channel markup.`,
    },
    {
      name: 'offer not blacklisted',
      passed: !!channelMarkup && !isBlacklisted,
      reason: isBlacklisted ? 'This offer is blacklisted from channel markup.' : undefined,
    },
    {
      name: 'config allows channel markup',
      passed: configAllowsChannelMarkup,
      reason: configDisabledReason,
    },
  ]

  const evaluations: ChannelMarkupEvaluation['evaluations'] = checks.map(check => ({
    name: check.name,
    passed: check.passed,
    ...(check.reason ? { reason: check.reason } : undefined),
  }))
  const unsupportedReason = checks.find(check => !check.passed)?.reason

  return {
    ...details,
    offerId,
    offerType,
    offerLabel,
    isSupported: !unsupportedReason,
    ...(unsupportedReason ? { unsupportedReason } : undefined),
    evaluations,
  }
}

export function getChannelMarkupValue(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): number {
  return getChannelMarkupDetails(offerId, offerType, channelMarkup).markup
}

export function getMemberPriceChannelMarkupDetails(
  offerId: string,
  offerType?: App.OfferType,
  channelMarkup?: App.ChannelMarkup,
): ChannelMarkupDetails {
  if (!config.ENABLE_CHANNEL_MARKUP_FOR_MEMBER_PRICE) {
    return NO_CHANNEL_MARKUP
  }
  if (!isOfferEligibleForChannelMarkup(offerId, offerType, channelMarkup)) {
    return NO_CHANNEL_MARKUP
  }

  const luxplusMarkup = channelMarkup?.luxplusMarkupValue
  if (!luxplusMarkup) {
    return NO_CHANNEL_MARKUP
  }
  return { markup: luxplusMarkup, isApplied: true }
}

export function isAllowedChannelMarkupQueryParams(filters: App.ChannelMarkupFilters) {
  return ALLOWED_UTM_SOURCE.includes(filters.source)
}

export function doesChannelMarkupMatchFilters(channelMarkup: App.ChannelMarkup, filters: App.ChannelMarkupFilters): boolean {
  return channelMarkup.source === filters.source
    && channelMarkup.medium === filters.medium
    && channelMarkup.campaign === filters.campaign
    && (channelMarkup.term ?? undefined) === (filters.term ?? undefined)
}

export function evaluateChannelMarkup(
  channelMarkups: Array<App.ChannelMarkup> | undefined,
  filters: App.ChannelMarkupFilters,
): App.ChannelMarkup | undefined {
  if (!channelMarkups?.length) {
    return undefined
  }

  return channelMarkups.find(channelMarkup => doesChannelMarkupMatchFilters(channelMarkup, filters))
}

export function applyChannelMarkupAndRoundUpToTen(value: number, markupDetails: ChannelMarkupDetails): number {
  if (!markupDetails.isApplied) {
    // No channel markup applied: return original price untouched.
    return value
  }

  const updatedValue = value * markupDetails.markup
  // Markups and markdowns both round UP to the nearest ten. A small markdown can round to/above base
  // (intended). Cross-service: svc-order must round markdowns up and tolerate that, or order creation
  // rejects the client price (hotel_invalid_price).
  return roundUpToNearestTen(updatedValue)
}
